import React, { useEffect, useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Badge, Input, Select, Field, Modal, Spinner, Textarea } from '../ui.jsx';

const ROLES = [
  { v:'new_lead_qualifier', l:'New Lead Qualifier' },
  { v:'estimate_follow_up', l:'Estimate Follow-Up' },
  { v:'winback_recovery', l:'Win-Back / Recovery' },
  { v:'collections', l:'Collections' },
  { v:'appointment_reminder', l:'Appointment Reminder' },
  { v:'review_request', l:'Review Request' },
];
const VOICES = ['maya','daniel','ryan','aaron','sara','susan','austin','derek','annie'];

export function Agents({ toast }) {
  const [data, setData] = useState({ agents:[], voiceConfigured:false });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [testing, setTesting] = useState(null);

  const load = () => api('/api/agents').then(setData).catch(e=>toast(e.message,'error')).finally(()=>setLoading(false));
  useEffect(() => { load(); }, []);

  const remove = async a => {
    if (!confirm(`Delete agent "${a.name}"?`)) return;
    try { await api(`/api/agents/${a.id}`, { method:'DELETE' }); load(); toast('Deleted'); }
    catch(e) { toast(e.message,'error'); }
  };

  if (loading) return <Spinner sz={32}/>;

  return <div style={{ padding:24 }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:18 }}>
      <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>AI Voice Agents</h1>
      <Btn v="primary" onClick={()=>setEditing({ role:'new_lead_qualifier', voiceId:'maya', language:'en', maxDurationMinutes:5, status:'active' })}>+ New Agent</Btn>
    </div>

    <OutboundCallSettings toast={toast}/>

    <TransferAndBookingSettings toast={toast}/>


    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(380px,1fr))', gap:14 }}>
      {data.agents.map(a => (
        <div key={a.id} style={styles.card}>
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:10 }}>
            <div>
              <strong style={{ fontSize:15 }}>{a.name}</strong>
              <Badge c={G.purple} bg={G.purpleDim} style={{ marginLeft:6 }}>{ROLES.find(r=>r.v===a.role)?.l || a.role}</Badge>
              {a.status==='active' && <Badge c={G.green} bg={G.greenLight} style={{ marginLeft:4 }}>active</Badge>}
            </div>
            <Btn sz="xs" v="danger" onClick={()=>remove(a)}>×</Btn>
          </div>
          <div style={{ fontSize:12, color:G.textMuted, marginBottom:8 }}>
            🎤 Voice: <strong style={{ color:G.text }}>{a.voiceId}</strong> · {a.language} · max {a.maxDurationMinutes}min
          </div>
          {a.firstMessage && <div style={{ ...styles.card, padding:10, background:G.input, fontSize:13, marginBottom:10, fontStyle:'italic', color:G.textMuted }}>
            "{a.firstMessage}"
          </div>}
          <div style={{ display:'flex', gap:14, fontSize:12, color:G.textMuted, marginBottom:10 }}>
            <span>📞 {a.stats?.total || 0} calls</span>
            <span>📅 {a.stats?.booked || 0} booked</span>
            {a.stats?.total > 0 && <span>{Math.round((a.stats.booked||0)/a.stats.total*100)}% conversion</span>}
          </div>
          <div style={{ display:'flex', gap:6 }}>
            <Btn sz="sm" onClick={()=>setEditing(a)}>Edit Script</Btn>
            <Btn sz="sm" v="cyan" onClick={()=>setTesting(a)}>📞 Test Call</Btn>
          </div>
        </div>
      ))}
      {data.agents.length === 0 && <div style={{ ...styles.card, padding:32, textAlign:'center', color:G.textMuted, gridColumn:'1/-1' }}>No agents yet. Spin up your first AI voice agent.</div>}
    </div>

    <AgentModal a={editing} onClose={()=>setEditing(null)} onSaved={()=>{ load(); setEditing(null); toast('Saved'); }}/>
    <TestModal a={testing} onClose={()=>setTesting(null)} toast={toast}/>
  </div>;
}

function AgentModal({ a, onClose, onSaved }) {
  const [f, setF] = useState({});
  useEffect(() => { setF(a||{}); }, [a]);
  if (!a) return null;
  const isNew = !f.id;
  const save = async () => {
    if (!f.name || !f.role) return alert('Name and role required');
    try {
      if (isNew) await api('/api/agents', { method:'POST', body: f });
      else await api(`/api/agents/${f.id}`, { method:'PATCH', body: f });
      onSaved();
    } catch(e) { alert(e.message); }
  };
  return <Modal open onClose={onClose} width={680}>
    <h2 style={{ margin:'0 0 14px 0' }}>{isNew?'New AI Agent':f.name}</h2>
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
      <Field label="Name *"><Input value={f.name||''} onChange={e=>setF({...f,name:e.target.value})}/></Field>
      <Field label="Role *">
        <Select value={f.role||'new_lead_qualifier'} onChange={e=>setF({...f,role:e.target.value})}>
          {ROLES.map(r => <option key={r.v} value={r.v}>{r.l}</option>)}
        </Select>
      </Field>
      <Field label="Voice">
        <Select value={f.voiceId||'maya'} onChange={e=>setF({...f,voiceId:e.target.value})}>
          {VOICES.map(v => <option key={v}>{v}</option>)}
        </Select>
      </Field>
      <Field label="Max Duration (min)"><Input type="number" value={f.maxDurationMinutes||5} onChange={e=>setF({...f,maxDurationMinutes:Number(e.target.value)})}/></Field>
      <Field label="Transfer Phone (optional)" style={{ gridColumn:'1/-1' }}><Input value={f.transferPhone||''} onChange={e=>setF({...f,transferPhone:e.target.value})} placeholder="+18135550100"/></Field>
    </div>
    <Field label="Opening line">
      <Input value={f.firstMessage||''} onChange={e=>setF({...f,firstMessage:e.target.value})} placeholder="Hi, this is Sarah with..."/>
    </Field>
    <Field label="Script / Goal" hint="Use {{first_name}}, {{service_type}}, {{business_name}} as variables.">
      <Textarea value={f.script||''} onChange={e=>setF({...f,script:e.target.value})} style={{ minHeight:200 }}/>
    </Field>
    <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
      <Btn onClick={onClose}>Cancel</Btn>
      <Btn v="primary" onClick={save}>Save</Btn>
    </div>
  </Modal>;
}

function TestModal({ a, onClose, toast }) {
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  if (!a) return null;
  const submit = async () => {
    if (!phone) return alert('Enter a phone number');
    setBusy(true);
    try { const r = await api(`/api/agents/${a.id}/test-call`, { method:'POST', body:{ phone } });
      toast(r.mock ? 'Mock call (ELEVENLABS_API_KEY not set)' : 'Test call initiated via ElevenLabs');
      onClose();
    } catch(e) { alert(e.message); setBusy(false); }
  };
  return <Modal open onClose={onClose} width={460}>
    <h2 style={{ margin:'0 0 8px 0' }}>📞 Test Call: {a.name}</h2>
    <div style={{ color:G.textMuted, fontSize:13, marginBottom:14 }}>The agent will call this number with the configured script.</div>
    <Field label="Phone Number"><Input value={phone} onChange={e=>setPhone(e.target.value)} placeholder="+18135550199"/></Field>
    <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
      <Btn onClick={onClose}>Cancel</Btn>
      <Btn v="primary" onClick={submit} disabled={busy}>{busy?<Spinner/>:'Place Call'}</Btn>
    </div>
  </Modal>;
}

function OutboundCallSettings({ toast }) {
  const [s, setS] = useState(null);
  const load = () => api('/api/agents/outbound-settings').then(setS).catch(()=>{});
  useEffect(() => { load(); }, []);
  if (!s) return null;
  const save = async (patch) => {
    try { await api('/api/agents/outbound-settings', { method:'PATCH', body: patch }); await load(); toast('Saved'); }
    catch (e) { toast(e.message, 'error'); }
  };
  return <div style={{ ...styles.card, padding:18, marginBottom:18, borderColor:G.purple+'66' }}>
    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
      <span style={{ fontSize:22 }}>📞</span>
      <h3 style={{ margin:0 }}>AI Outbound Calling</h3>
      <Badge c={s.outboundEnabled?G.green:G.textMuted} bg={(s.outboundEnabled?G.green:G.textMuted)+'22'}>
        {s.outboundEnabled ? 'ENABLED' : 'DISABLED'}
      </Badge>
    </div>
    <div style={{ fontSize:13, color:G.textMuted, lineHeight:1.55 }}>
      Inbound calls are always handled by the AI. <strong>Outbound</strong> is opt-in — when on, the AI agent can call leads automatically (workflow steps) or on-demand ("🤖 Call with AI" on any contact).
    </div>
    {!s.phoneConfigured && <div style={{ marginTop:10, padding:10, background:G.yellowLight, borderRadius:8, fontSize:12 }}>
      ⚠️ Import your Twilio number into ElevenLabs first (Integrations → ElevenLabs Voice AI → Import Twilio Number). Outbound won't work without it.
    </div>}
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:14, marginTop:14 }}>
      <Field label="Outbound calling">
        <label style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 12px', background:G.input, borderRadius:8, cursor:'pointer' }}>
          <input type="checkbox" checked={s.outboundEnabled} onChange={e=>save({ enabled: e.target.checked })}/>
          <span>Enable outbound AI calls</span>
        </label>
      </Field>
      <Field label="Earliest call time">
        <Input type="time" value={s.quietStart} onChange={e=>save({ quietStart: e.target.value })}/>
      </Field>
      <Field label="Latest call time">
        <Input type="time" value={s.quietEnd} onChange={e=>save({ quietEnd: e.target.value })}/>
      </Field>
    </div>
    <div style={{ fontSize:11, color:G.textDim, marginTop:8 }}>
      TCPA recommends calling only between 8 AM and 9 PM in the recipient's local time. We default to 8 AM–8 PM ET; tighten as needed.
    </div>
  </div>;
}

function TransferAndBookingSettings({ toast }) {
  const [s, setS] = useState(null);
  const load = () => api('/api/agents/transfer-settings').then(setS).catch(()=>{});
  useEffect(() => { load(); }, []);
  if (!s) return null;
  const save = async (patch) => {
    try { await api('/api/agents/transfer-settings', { method:'PATCH', body: patch }); await load(); toast('Saved'); }
    catch (e) { toast(e.message, 'error'); }
  };
  return <div style={{ ...styles.card, padding:18, marginBottom:18, borderColor:G.cyan+'66' }}>
    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
      <span style={{ fontSize:22 }}>📅</span>
      <h3 style={{ margin:0 }}>AI Booking + Human Handoff</h3>
    </div>
    <div style={{ fontSize:13, color:G.textMuted, lineHeight:1.55, marginBottom:12 }}>
      AI agents (SMS, inbound calls, outbound calls) book appointments straight onto the calendar + can transfer the caller / SMS thread to a human when the conversation goes beyond scheduling.
    </div>
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
      <Field label="Transfer phone (where calls escalate)">
        <Input placeholder="+18135550100" value={s.transferPhone||''} onChange={e=>save({ transferPhone: e.target.value })}/>
      </Field>
      <Field label="Transfer recipient name (for SMS alert)">
        <Input placeholder="Mike" value={s.transferName||''} onChange={e=>save({ transferName: e.target.value })}/>
      </Field>
      <Field label="Default service for AI bookings">
        <Input placeholder="Service Call" value={s.defaultService||''} onChange={e=>save({ defaultService: e.target.value })}/>
      </Field>
      <Field label="Default duration (minutes)">
        <Input type="number" value={s.defaultDurationMin||120} onChange={e=>save({ defaultDurationMin: Number(e.target.value) })}/>
      </Field>
    </div>
    <div style={{ fontSize:11, color:G.textDim, marginTop:8 }}>
      Voice transfer uses ElevenLabs Conv AI's built-in transfer feature → caller routed to the number above. SMS handoff sends an alert to that number with the customer's last message so a human can take over.
    </div>
  </div>;
}
